/**
 * DateRangeSelector — the period dropdown in the top bar's scope group.
 * Presets + custom range from DateRangeContext; token-driven so it theme-switches.
 */

import React, { useState, useRef, useEffect } from 'react'
import { X } from 'lucide-react'
import Sym from './Sym'
import { format, isValid } from 'date-fns'
import { useDateRange, DATE_PRESETS } from '../contexts/DateRangeContext'
import clsx from 'clsx'

const inputClasses =
  'w-full px-3 py-2 text-sm rounded-md border bg-bg border-line text-ink focus:outline-none focus:ring-2 focus:ring-brand'

const DateRangeSelector = () => {
  const { dateRange, isCustomRange, setPresetRange, setCustomRange, getFormattedRange } = useDateRange()

  const [isOpen, setIsOpen] = useState(false)
  const [showCustom, setShowCustom] = useState(false)
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setShowCustom(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (showCustom && dateRange.startDate && dateRange.endDate) {
      setCustomStart(format(dateRange.startDate, 'yyyy-MM-dd'))
      setCustomEnd(format(dateRange.endDate, 'yyyy-MM-dd'))
    }
  }, [showCustom, dateRange])

  const handlePreset = (key) => {
    setPresetRange(key)
    setIsOpen(false)
    setShowCustom(false)
  }

  const handleCustomSubmit = (e) => {
    e.preventDefault()
    const start = new Date(customStart)
    const end = new Date(customEnd)
    if (isValid(start) && isValid(end) && start <= end) {
      setCustomRange(start, end)
      setIsOpen(false)
      setShowCustom(false)
    }
  }

  const formattedRange = getFormattedRange()

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'cx-btn flex h-[30px] items-center gap-1.5 rounded-lg border border-line bg-panel px-[11px] text-[12.5px] text-mut',
          isOpen && 'ring-2 ring-brand'
        )}
      >
        <span>{formattedRange}</span>
        <Sym name="expand_more" size={16} className={clsx('transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl bg-panel border border-line shadow-cx z-50">
          {!showCustom ? (
            <div className="py-2">
              <div className="px-4 py-2 font-mono text-[10px] tracking-[.1em] uppercase text-mut2">Quick select</div>
              {Object.entries(DATE_PRESETS).map(([key, preset]) => (
                <button
                  key={key}
                  onClick={() => handlePreset(key)}
                  className={clsx(
                    'w-full text-left px-4 py-2 text-sm transition-colors hover:bg-panel2',
                    dateRange.preset === key ? 'bg-info-bg text-brand font-medium' : 'text-ink'
                  )}
                >
                  {preset.label}
                </button>
              ))}
              <div className="border-t border-line2 my-1" />
              <button
                onClick={() => setShowCustom(true)}
                className={clsx(
                  'w-full text-left px-4 py-2 text-sm transition-colors hover:bg-panel2',
                  isCustomRange && !dateRange.preset ? 'bg-info-bg text-brand font-medium' : 'text-ink'
                )}
              >
                Custom range…
              </button>
            </div>
          ) : (
            <form onSubmit={handleCustomSubmit} className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-ink">Select date range</h3>
                <button type="button" onClick={() => setShowCustom(false)} className="text-mut hover:text-ink">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-3">
                <label className="block">
                  <span className="block text-xs font-medium text-mut mb-1">Start date</span>
                  <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className={inputClasses} required />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-mut mb-1">End date</span>
                  <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} min={customStart} className={inputClasses} required />
                </label>
              </div>
              <div className="flex gap-2 mt-4">
                <button type="button" onClick={() => setShowCustom(false)} className="flex-1 px-3 py-2 text-sm font-medium rounded-md bg-panel2 text-ink hover:opacity-80 transition">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-3 py-2 text-sm font-medium rounded-md bg-brand text-white hover:opacity-90 transition">
                  Apply
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  )
}

export default DateRangeSelector
